import { AppConfig } from '../../../Config';
import { ImageData } from '../Rest/Services/ItemService/Data';
import { Item } from '../Rest/Services/Data';

export const BOOK_COVER_PLACEHOLDER = `${process.env.PUBLIC_URL || ''}/book-cover-placeholder.svg`;

type ImageVariant = 'thumb' | 'medium' | 'original';

const normalizeIsbn = (isbn?: string) => isbn?.replace(/[^0-9Xx]/g, '');

export const toAbsoluteUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;

  if (/^https?:\/\//i.test(AppConfig.ReaderlyApiUrl)) {
    return new URL(url, AppConfig.ReaderlyApiUrl).toString();
  }

  return url.startsWith('/') ? url : `/${url}`;
};

const getImageUrl = (image?: ImageData, variant: ImageVariant = 'medium'): string | undefined => {
  if (!image) return undefined;

  const variantUrl =
    variant === 'thumb'
      ? image.thumbUrl
      : variant === 'original'
        ? image.originalUrl
        : image.mediumUrl;

  return toAbsoluteUrl(variantUrl || image.url || image.mediumUrl || image.thumbUrl || image.originalUrl);
};

type BookImageSource = Pick<Item, 'id' | 'image'> & {
  isbn?: string;
  primaryImage?: ImageData;
  images?: ImageData[];
};

export const getBookImageUrl = (item: BookImageSource, variant: ImageVariant = 'medium'): string => {
  if (item.image) return item.image;

  const primaryImage = getImageUrl(item.primaryImage, variant);
  if (primaryImage) return primaryImage;

  const imageFromCollection = item.images?.map((image) => getImageUrl(image, variant)).find(Boolean);
  if (imageFromCollection) return imageFromCollection;

  const isbn = normalizeIsbn(item.isbn);
  if (isbn) {
    return `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg?default=false`;
  }

  return BOOK_COVER_PLACEHOLDER;
};

const getPrimaryImage = (item: BookImageSource): ImageData | undefined => {
  return item.primaryImage || item.images?.[0];
};

export const getBookImageSrcSet = (item: BookImageSource): string | undefined => {
  if (item.image || normalizeIsbn(item.isbn)) return undefined;

  const image = getPrimaryImage(item);
  if (!image) return undefined;

  const sources = [
    [image.thumbUrl, 360],
    [image.mediumUrl, 900],
    [image.originalUrl, 1600],
  ] as const;

  return sources
    .map(([url, width]) => {
      const absoluteUrl = toAbsoluteUrl(url);
      return absoluteUrl ? `${absoluteUrl} ${width}w` : undefined;
    })
    .filter(Boolean)
    .join(', ') || undefined;
};

export const imageStorageHint =
  'Images are stored as optimized local variants. Catalog cards use responsive image sources, details use medium.';
